import Commons from "oc-commons/Commons.js";
import DeviceDetection from "oc-commons/DeviceDetection.js";
import I18N from "oc-I18N";
import Dialogs from "oc-ui/dialogs/Dialogs.js";
import Urls from "oc-commons/Urls.js";
import AbstractApp from "./AbstractApp.js";
import HBS from "./hbs/HBS.js";
import Cookies from "oc-commons/Cookies.js";
import "./AppHandlebarsExtensions.js";
import Wkfg2BackendApi from "./Wkfg2BackendApi.js";
import SafePromise from "oc-commons/SafePromise";
import Mimetypes from "oc-commons/Mimetypes";
import UrlFluid from "oc-commons/UrlFluid";

/**
 * Erweitert die AbstractApp um eine Standard-WKFG-Backend-API. Die eigentliche API ist dabei vom Backend in Form
 * von JSON Ajax Requests festgelegt. Diese API hier fügt weitere Struktur, Semantik und UI-Verhalten hinzu.
 * In Ableitungen von AbstractApp können diese Methode überschrieben werden.
 *
 * Folgende Events werden auf APP ausgelöst:
 *
 * - **loadedMetadata** nachdem APP.loadMetadata() erfolgreich ist
 *
 * - **loadedInitialConfiguration** nachdem APP.loadInitialConfiguration() erfolgreich ist
 * - **loadedConfiguration** immer nachdem eine Configuration geladen wurde
 *
 * - **beforeSaveConfiguration** kurz bevor APP.save() die Configuration wirklich an den Server überträgt, als Hook zur Veränderung der Configuration
 * - **savedConfiguration** nachdem APP.save() die Configuration gespeichert hat
 *
 * - **changedConfiguration** nach dem Laden der APP
 *
 * - **loadedMetamodel** nach dem Laden des Metamodell (optional)
 *
 */
Commons.monkeyPatch(AbstractApp.prototype, {

    /**
     * Gibt true zurück, falls die Configuration vom Anwender verändert wurde.
     * Es wird hierbei nicht mit Dirty-Flags gearbeitet, sondern der Snapshot des zuletzt geladenen /
     * gespeicherten Stand wird mit der aktuellen Configuration verglichen.
     */
    isConfigurationChanged() {

        const now = APP._configurationSnapshot;
        const snapshot = APP.getStageAsConfiguration();

        const nowString = JSON.stringify(now);
        const snapshotString = JSON.stringify(snapshot);

        // if (nowString != snapshotString) {
        //     console.log(Commons.deepDiff(now, snapshot), now, snapshot);
        // }

        return nowString != snapshotString;
    },

    /**
     * Gibt zurück, welche "Snapshots" (Standardansichten) von der Konfiguration beim Speichern "geschossen" werden sollen.
     * @example
     * return [{
     *    // der Dateiname, die Endung '.jpg' wird angefügt
     *    name: 'Vorderansicht',
     *    // die weiteren Parameter kommen von STAGE.setCameraAndRenderConfigAndReturnUndo
     *    direction: { x: 1, y: 1, z: 1 },
     *    hideObjectsByName: ['ground','coordinateOffset','BoxRuler'],
     *    zoomTo: true,
     *    extraZoom: 0.7,
     *    backgroundColor: 0xFFFFFF,
     *    backgroundAlpha: 1,
     * }];
     */
    configurationFotoSnapshots() {
        // Hier nur als Default
        return [
            {
                name: 'Vorderansicht',
                direction: {x: 0, y: 0.2, z: 1},
                object: {x: 0, y: 0, z: 0},
                hideObjectsByName: ['ground', 'coordinateOffset'],
                zoomTo: true,
                extraZoom: 1,
                backgroundColor: 0xFFFFFF,
                backgroundAlpha: 1,
                renderWidth: 800,
                renderHeight: 496,
                mimeType: 'image/jpeg',
            },
            {
                name: 'Hinteransicht',
                direction: {x: 0, y: 0.2, z: -1},
                object: {x: 0, y: 0, z: 0},
                hideObjectsByName: ['ground', 'coordinateOffset'],
                zoomTo: true,
                extraZoom: 1,
                backgroundColor: 0xFFFFFF,
                backgroundAlpha: 1,
                renderWidth: 800,
                renderHeight: 496,
                mimeType: 'image/jpeg',
            },
        ];
    },

    /**
     * Gibt die URL des Standard-Bildes (FotoSnapshot) der Configuration zurück.
     */
    getConfigurationStandardImageUrl() {
        return this.getConfigurationContentUrl() + '/Foto.jpg';
    },

    /**
     * Lädt die Metadata (meist die relationalen Daten der Produkte, Materialien, etc.)
     */
    loadMetadata(query) {
        return this.wkfgBackendApi.loadMetadata(query).then(data => {
            this.checkMetadataMustBeSet(data);
            APP.metadata = data;
            //console.log('APP.metadata=', APP.metadata);
            APP.trigger('loadedMetadata');
            return APP.metadata;
        });
    },

    /**
     * Lädt eine Configuration vom Backend. configurationQuery ist dabei der Query-URL-Teil, der an
     * das Konfiguration-Backend-API weitergeleitet wird
     */
    loadConfiguration(configurationQuery, successCallback = undefined, failureCallback = undefined) {

        const notFound = (error) => /no record matching/g.test(error.message);

        const defaultFailureCallback = (error) => {
            if (notFound(error)) {
                Dialogs.showError(I18N('NothingFoundAtThisAdress_GoBack', APP.settings.homeUrl));
            } else {
                throw error;
            }
        }

        return this.wkfgBackendApi
            .loadConfiguration(configurationQuery)
            .defaultCatch(failureCallback || defaultFailureCallback)
            .then(configuration => {
                // deepclone, weil der K2 z.B. beim Startup gerne die Konfiguration manuell anpasst (Regeln).
                console.log('load configuration', _.cloneDeep(configuration));
                if (!configuration) {
                    Dialogs.showError(I18N('NothingFoundAtThisAdress_GoBack', APP.settings.homeUrl));
                    return;
                }
                if (successCallback) {
                    successCallback(configuration);
                }
                this._setConfigurationCookie();
                APP.trigger('changedConfiguration');
                APP.trigger('loadedConfiguration');
                this._setConfigurationSnapshot();
            });
    },

    /**
     * Prüft, ob der URL-Query-String gesetzt ist. Wenn ja, dann wird der QueryString an die Konfiguration-Backend-API
     * weitergeleitet und die entsprechende Konfiguration geladen (meist &where.Code=xxxxxxx). Sonst wird eine
     * leere Konfiguration gesetzt.
     * Sollte in den Settings ein Code gesetzt wird diese Konfiguration als Startkonfiguration genutzt.
     */
    loadInitialConfiguration() {
        // mit diesem delayed setConfigurationSnapshot wird erreicht, dass auch Aufräumarbeiten an der Configuration
        // nach dem Laden, die bei komplexen Apps wie dem Zaunplaner laufen, nicht als User-Änderungen gewertet werden
        APP.onOnce('ready', () => {
            _.delay(() => {
                this._setConfigurationSnapshot();
            }, 1000);
        });

        const code = _.isEmpty(Urls.urlParam('Code')) ? this.settings.Code : Urls.urlParam('Code');
        const id = Urls.urlParam('id');
        let searchStringStripped = '';
        if (!_.isEmpty(code)) {
            searchStringStripped = '&where.Code=' + code;
        }
        else if (!_.isEmpty(id)) {
            searchStringStripped = '&where.id=' + id;
        }

        if (searchStringStripped === '') {
            this.setStageFromConfiguration(null);
            this._setConfigurationCookie();
            APP.trigger('changedConfiguration');
            APP.trigger('loadedConfiguration');
            APP.trigger('loadedInitialConfiguration');
            this._setConfigurationSnapshot();
        } else {
            this.loadConfiguration(searchStringStripped, (configuration) => {
                this.setStageFromConfiguration(configuration);
                APP.trigger('loadedInitialConfiguration');
            });
        }
    },

    updatePrices( callback) {
        this.wkfgBackendApi.updatePrices(APP.getStageAsConfiguration())
            .then(result=>callback(result));
    },


    /**
     * Schaltet eine Warnung ein, falls die Konfiguration geändert, aber nicht gespeichert wurde, und
     * der User den Konfigurator verläßt
     */
    addOnUnloadWarning() {
        if (APP.settings.userRole == 'admin') {
            return;
        }
        $(window).on("beforeunload", () => {
            if (!window.WAS_UNRECOVERABLE_ERROR && APP.isConfigurationChanged()) {
                return I18N('YouHaveUnsavedChanges');
            }
        });
    },

    /**
     * Prüft, ob Metadaten existieren. Eine leere Metadaten-DB ist ein Fehler. Falls man das nicht will, hier
     * überschreiben.
     */
    checkMetadataMustBeSet(data) {
        if (_.isEmpty(data) || _.every(_.values(data), (x) => _.isEmpty(x))) {
            console.error('Metadata is empty! result from server was: ', data);
            // throw exception("ServerCommunicationFailure", I18N('ProductDbIsEmpty'));
        }
    },

    /**
     * Setzt einen Domain-weiten Cookie
     */
    setCookie(cookie_name, value) {
        Cookies.setItem(cookie_name, value, undefined, '/');
        // console.log("set " + cookie_name + "='" + Cookies.getItem(cookie_name) + "'");
    },

    /**
     * Gibt die Url auf das Content-Verzeichnis zur Configuration zurück
     */
    getConfigurationContentUrl() {
        return APP.settings.wkfgContentBaseUrl + 'Konfiguration/' + this.getCurrentConfiguration().Code;
    },

    /**
     * Teilt die Configuration auf Facebook. Die Konfiguration wird erst gespeichert, und entsprechende Dialoge öffnen sich.
     */
    shareFacebook() {
        const myURL = APP.settings.configuratorUrl + '?Code=' + this.getCurrentConfiguration().Code;
        this._shareSocial('Facebook', APP.settings.pleaseWaitFacebookUrl,
            'http://www.facebook.com/sharer/sharer.php?u=' + encodeURIComponent(myURL) + '&t=' + I18N('App-Name'));
    },

    /**
     * Teilt die Configuration auf Pinterest. Die Konfiguration wird erst gespeichert, und entsprechende Dialoge öffnen sich.
     */
    sharePinterest() {
        const myURL = APP.settings.configuratorUrl + '?Code=' + this.getCurrentConfiguration().Code;
        const myImg = this.getConfigurationStandardImageUrl();
        this._shareSocial('Pinterest', APP.settings.pleaseWaitPinterestUrl,
            'http://pinterest.com/pin/create/button/?url=' + encodeURIComponent(myURL) + '&media=' + encodeURIComponent(myImg) + '&description=' + I18N('App-Name'));
    },

    /**
     * Speichert die Konfiguration und erzeugt ein PDF, das heruntergeladen bzw. im Browser geöffnet wird.
     */
    pdf(callback = null, options = {}) {

        if (this._isValidationErrorAndMaybeShowError(I18N('ausgedruckt'))) {
            return;
        }

        STAGE.setRenderOffline(true);

        const waitingDialog = Dialogs.showWaiting(I18N('das PDF wird erzeugt...'));

        options = Commons.defaultsDeepSafe({}, options, {
            callback: (configuration) => {
                STAGE.setRenderOffline(false);

                // füge Url-Parameter der Location und options.additionalParameter an die PDF-Url an
                const pdfUrlFluid = new UrlFluid(this.wkfgBackendApi.getPdfUrl(configuration.Code, false));
                Urls.getParameters().filter(pair=>/tenant|lang/.test(pair[0])).forEach(pair=>{
                    pdfUrlFluid.param(pair[0],pair[1]);
                });
                if (!Commons.isEmpty(options.additionalParameter)){
                    Object.keys(options.additionalParameter).forEach(k=>{
                        pdfUrlFluid.param(k,options.additionalParameter[k]);
                    })
                }
                let pdfUrl = pdfUrlFluid.toString();
                console.log('pdf wird erzeugt: ', pdfUrl);

                if (DeviceDetection.isDesktop()) {

                    const iframe = $('<iframe id="printIFrame" width="100px" height="100px" src="' + pdfUrl + '"/>');
                    $('body').append(iframe);
                    _.delay(() => {
                        this._setConfigurationCookie();
                        waitingDialog.hide();
                        Dialogs.showToast(I18N('das PDF wurde heruntergeladen'), 5000);
                        _.delay(() => iframe.remove(), 10000);
                    }, 2000);
                } else {
                    waitingDialog.hide();
                    Dialogs.showOk(I18N('Sie können das PDF über folgenden Link öffnen: <br/><br/> <a', pdfUrl, pdfUrl));
                }
                if (callback) callback();
            },
            onCancel:()=> {
                waitingDialog.hide();
                STAGE.setRenderOffline(false);
            },
            preferUpdate: true,
            quiet: true,
        });
        if (this.configuration.Locked && !this.isConfigurationChanged()) {
            options.callback(this.configuration);
        }
        else {
            this.save(options);
        }
    },

    /**
     * Speichert die Konfiguration.
     * @param options siehe Code
     */
    save(options) {
        options = Commons.defaultsDeepSafe({}, options, {
            // wird bei Erfolg aufgerufen
            callback: (configuration) => {
            },
            // Wird innerhalb des blurs aufgerufen, muss cb aufrufen
            inSaveCallback: (cb) => cb(),
            // falls die Wahl zwischen Update oder Create besteht, soll automatisch update genommen werden?
            preferUpdate: false,
            // Hiermit kann ein create erzwungen werden
            // Wenn preferUpdate: false und forceNoUpdate wird ein create erzwungen
            forceNoUpdate: false,
            // sollen keine Meldungen ausgegeben werden?
            quiet: false,
            // sollen keine Bilder generiert werden?
            noFotoSnapshots: false,
            // callback, der vor dem Speichern aufgerufen wird (in method steht 'create' oder 'update')
            modifyBeforeWrite: (configuration, method) => {
            },
            onCancel:()=>{}
        });

        const configuration = APP.getStageAsConfiguration();

        if (this._isValidationErrorAndMaybeShowError('CantSaveTheConfigurationBecauseOfTheFollowingErrors')) {
            return;
        }

        const create = 'create';
        const update = 'update';
        const create_or_update = 'create_or_update';

        const decision_table = {
            admin: {
                default_config: create,
                inspiration_config: create_or_update,
                admin_config: create_or_update,
                customer_config: create_or_update,
                locked_config: create_or_update
            },
            haendler: {
                default_config: create,
                inspiration_config: create,
                admin_config: create,
                customer_config: create_or_update,
                locked_config: create_or_update
            },
            kunde_eingeloggt: {
                default_config: create,
                inspiration_config: create,
                admin_config: create,
                customer_config: create_or_update,
                locked_config: create
            },
            kunde_nicht_eingeloggt: {
                default_config: create,
                inspiration_config: create,
                admin_config: create,
                customer_config: create_or_update,
                locked_config: create
            },
        };

        const role = APP.settings.userRole;

        const configurationType = this.getConfigurationType(configuration);
        let decision = decision_table[role][configurationType];

        if (options.preferUpdate && decision == create_or_update) {
            decision = update;
        } else if(!options.preferUpdate && options.forceNoUpdate) {
            decision = create;
        }

        // console.info('save decision=' + decision + ', role=' + role + ' configurationType=' + configurationType);

        if (configurationType == 'locked_config') {
            if (decision == create) {
                Dialogs.askQuestion(I18N('LockedCreateANewConfiguration'), [{
                    label: I18N('CreateNew'),
                    primary: true,
                    action: () => {
                        configuration.Locked=false;
                        this._saveAction(configuration, create, options.callback, options.quiet, options.noFotoSnapshots, options.modifyBeforeWrite, options.inSaveCallback);
                    }
                }, {
                    label: I18N('Cancel'),
                    action: () => {options.onCancel()}
                }]);
            } else{
                //decission == create_or_update || update;
                Dialogs.askQuestion(I18N('LockedOverrideOrCreateNew'), [
                    {
                        label: I18N('CreateNew'),
                        primary: true,
                        action: () => {
                            configuration.Locked = false;
                            this._saveAction(configuration, create, options.callback, options.quiet, options.noFotoSnapshots, options.modifyBeforeWrite, options.inSaveCallback);
                        }
                    },
                    {
                        label: I18N('Overwrite'),
                        action: () => this._saveAction(configuration, update, options.callback, options.quiet, options.noFotoSnapshots, options.modifyBeforeWrite, options.inSaveCallback)
                    },
                    {
                        label: I18N('Cancel'),
                        action: () => {
                            options.onCancel()
                        }
                    }
                ]);
            }
        } else if (decision == create_or_update) {
            Dialogs.askQuestion(I18N('WouldYouLikeToReplaceOrAddTheConfiguration'), [{
                label: I18N('Overwrite'),
                primary: true,
                action: () => this._saveAction(configuration, update, options.callback, options.quiet, options.noFotoSnapshots, options.modifyBeforeWrite, options.inSaveCallback)
            }, {
                label: I18N('CreateNew'),
                action: () => this._saveAction(configuration, create, options.callback, options.quiet, options.noFotoSnapshots, options.modifyBeforeWrite, options.inSaveCallback)
            }/*, {
                label: I18N('Cancel'),
                action: () => {
                }
            }*/]);
        } else {
            this._saveAction(configuration, decision, options.callback, options.quiet, options.noFotoSnapshots, options.modifyBeforeWrite, options.inSaveCallback);
        }
    },

    /**
     * ermittelt die Art der Konfiguration, kann sein:
     * - 'default_config' die Konfiguration wurde nicht geladen sondern entspricht dem programmatischen Default / "Leer"
     * - 'inspiration_config' die Konfiguration ist eine Inspiration (Inspiration=true) und wurde vom Händler erstellt
     * - 'customer_config' die Konfiguration wurde vom Kunden erstellt
     * - 'admin_config' die Konfiguration wurde vom Admin erstellt
     */
    getConfigurationType(configuration) {
        if (typeof configuration.id === 'undefined') {
            return 'default_config';
        } else if (configuration.Inspiration) {
            return 'inspiration_config';
        } else if (configuration.Locked) {
            return 'locked_config';
        } else if (configuration.UserGuid == APP.settings.userGuid) {
            return 'customer_config';
        } else {
            return 'admin_config';
        }
    },

    /**
     * Lädt die Beschreibung des Metamodells und der Metadaten in einem, und gibt ein Promise zurück
     * Hinweis: Best Practise ist es, die I18N-Texte nicht über diesen Weg, sondern direkt als Teil der HTML-Seite auszuliefern,
     * damit z.B. der Loading-Screen oder Fehlermeldungen in der richtigen Sprache erscheinen.
     * @returns {SafePromise} {
     *    Konfiguration: {
     *        id: 1,
     *        Code: "MBN443QTX",
     *        ...
     *    },
     *    metadata: {
     *        MaterialPbr: [
     *          {
     *              id: 1,
     *              name: "Leder",
     *              ...
     *          },
     *          ...
     *        ]
     *    },
     *    metamodell: {
     *       languagesI18N: ["de","en"],
     *       tables: [
     *          {
     *              name: "Konfiguration",
     *              label: "Konfigurationen",
     *              ...
     *          },
     *          ...
     *       }
     *    },
     *    i18n_json: {
     *       "__shortname": "de",
     *       "number_dec_point": ",",
     *       "3 pro Seite": "3 pro Seite",
     *       ...
     *    },
     *    content_url: 'http://localhost/wp-content/uploads/wkfgcontent',
     * }
     */
    loadConfigData(options) {
        return this.wkfgBackendApi.loadConfigData(options).then((data) => {
            APP.metamodel = data.metamodel;
            APP.metadata = data.metadata;
            APP.configuration = _.get(data,'Konfiguration[0]');
        });
    },

    loadInitialConfigurationFromApp() {
        APP.trigger('loadedMetamodel');
        const initialConfiguration = APP.configuration;
        this.setStageFromConfiguration(initialConfiguration);
        if (initialConfiguration==null) {
            this._setConfigurationCookie();
            APP.trigger('changedConfiguration');
            APP.trigger('loadedConfiguration');
            APP.trigger('loadedInitialConfiguration');
            this._setConfigurationSnapshot();
        } else {
            APP.trigger('loadedInitialConfiguration');
        }
    },

    /**
     * Lädt die Beschreibung des Metamodells - also den Inhalt der metamodel.json
     * @param callback
     */
    loadMetamodel(callback = (metamodell) => {
    }) {
        return this.wkfgBackendApi.loadMetamodel().then((data) => {
            APP.metamodel = data;
            callback(APP.metamodel);
            APP.trigger('loadedMetamodel');
            return APP.metamodel;
        });
    },

    /**
     * Löscht eine Konfiguration mitsamt Content
     * @param id
     * @param callback
     */
    deleteConfiguration(id, callback = () => {
    }) {
        return this.wkfgBackendApi.deleteMetadata('Konfiguration', id).then(callback);
    },

    /**
     * @private
     */
    _saveAction(configuration, method, callback = (configuration) => {
    }, quiet = false, noFotoSnapshots = false, modifyBeforeWrite = () => {
    }, inSaveCallback = (cb) => cb()) {

        const saveIntern = () => {

            let freeze = $('<div>');
            let waitingDialog = $('<div>');

            if (!quiet) {
                waitingDialog = Dialogs.showWaiting(I18N('saving...'));
            }

            if (!noFotoSnapshots) {
                freeze = STAGE.freezeRendererAndBlur();
                STAGE.stopAnimation = true;
                if (APP.representer) {
                    APP.representer.disabled = true;
                }
            }
            _.delay(() => {
                configuration.UserGuid = APP.settings.userGuid;
                configuration.camera = STAGE.getCurrentCameraConfig();

                modifyBeforeWrite(configuration, method);

                //console.log('configuration before save ', configuration);
                this.wkfgBackendApi.saveMetadata('Konfiguration', method, configuration).then((configurationFromServer) => {
                    const setBrowserUrlWithoutReload = () => {
                        const configuration = APP.getStageAsConfiguration();
                        const code = configuration.code ? configuration.code() : configuration.Code;
                        let configuratorUrl = Urls.addOrUpdateQsParameter(window.location.href,'Code',code);
                        if (!Commons.isEmpty(code) && !Commons.isEmpty(configuratorUrl)) {
                            // Unter FF kann ein NS_ERROR_ILLEGAL_VALUE aufreten der nicht gecached werden
                            // kann wenn der String > 640 kb ist, daher compress
                            history.pushState(null, "", configuratorUrl);
                        }
                    };

                    console.log('configurationFromServer', configurationFromServer);

                    // transactional: erst jetzt, wo der Server die Config wirklich gespeichert hat, daten übertragen
                    this.updateConfigurationWithUnchangedJson(configurationFromServer);

                    setBrowserUrlWithoutReload();

                    // Sketches: Grafiken, die aus einem Canvas erzeugt werden.
                    // APP.uploadSketches sollte ein Array mit Objekten der Form {name: String, canvasPromise: Promise}
                    // zurückgeben. Das canvasPromise selbst löst zu einem Canvas-Objekt auf.
                    if (this.uploadSketches) {
                        const sketchDatas = this.uploadSketches()?.filter(sketchData => sketchData?.canvasPromise != null);
                        const sketchPromises = sketchDatas.map(sketchData => sketchData.canvasPromise);
                        Promise.all(sketchPromises).then((canvases) => {
                            Promise.all(canvases.map(canvas => new Promise(resolve => canvas.toBlob(resolve)))).then(canvasBlobs => {
                                const filesArray = canvasBlobs.map((canvasBlob, index) => {
                                    return {fileName: sketchDatas[index].name, blob: canvasBlob};
                                });
                                this.wkfgBackendApi.uploadContent('Konfiguration/' + configurationFromServer.Code, filesArray).then(() => {}, () => {
                                    console.log("Sketches upload fehlgeschlagen", sketchDatas);
                                });
                            });
                        })
                    }

                    const appendFileNameAndBlobForUpload = (options, fileNameAndBlobsArray, uploadCallback) => {
                        if (APP.imageStack){
                            //2D Konfigurator
                            _.delay(() => {
                                STAGE2D.setRendererToSize(options.renderWidth, options.renderHeight);
                                const undo=()=>STAGE2D.undo();
                                STAGE2D.renderToBlob(options.mimeType, (blob) => {
                                    undo();

                                    const formData = new FormData();
                                    const suffix = "." + Mimetypes.getSuffixOfMimetype(options.mimeType);

                                    fileNameAndBlobsArray.push({fileName: options.name + suffix, blob: blob});
                                    uploadCallback();
                                });
                            }, options.timeBeforeRender!=null?options.timeBeforeRender:1000);

                        } else if (options.glb===true){
                            STAGE.gltfToBlob((blob)=>{
                                fileNameAndBlobsArray.push({fileName: options.name+'.glb', blob: blob});
                                uploadCallback();
                            });

                        } else {
                            // console.log('appendFileNameAndBlobForUpload',options);
                            STAGE.setRendererToSize(options.renderWidth, options.renderHeight);
                            const undo = STAGE.setCameraAndRenderConfigAndReturnUndo(options);

                            _.delay(() => {
                                STAGE.renderToBlob(options.mimeType, (blob) => {
                                    undo();

                                    const formData = new FormData();
                                    const suffix = "." + Mimetypes.getSuffixOfMimetype(options.mimeType);

                                    fileNameAndBlobsArray.push({fileName: options.name + suffix, blob: blob});
                                    uploadCallback();
                                });
                            }, options.timeBeforeRender!=null?options.timeBeforeRender:1000);
                        }
                    };

                    if (!noFotoSnapshots) {
                        const fileNameAndBlobsArray = [];
                        const snapshots = _.map(_.clone(this.configurationFotoSnapshots()), (x) => {
                            return _.curry(appendFileNameAndBlobForUpload)(x, fileNameAndBlobsArray);
                        });

                        const callSnaphots = (snapshots) => {
                            if (snapshots.length > 0) {
                                const fn = _.head(snapshots);
                                fn(_.partial(callSnaphots, _.slice(snapshots, 1)));
                            } else {
                                this.wkfgBackendApi.uploadContent('Konfiguration/' + configurationFromServer.Code, fileNameAndBlobsArray);
                                STAGE.setRendererToSize();
                                STAGE.stopAnimation = false;
                                if (APP.representer) {
                                    APP.representer.disabled = false;
                                }
                                APP.trigger('savedConfiguration');
                                this._setConfigurationCookie();
                                this._setConfigurationSnapshot();
                                const waitingTimeForServerProcessing = 1000 + fileNameAndBlobsArray.length * 300;
                                _.delay(() => {
                                    if (!quiet) {
                                        APP.showSavedDialog(configurationFromServer);
                                    }
                                    inSaveCallback(() => {
                                        waitingDialog.hide();
                                        freeze.hide();
                                        callback(configurationFromServer);
                                    })
                                }, waitingTimeForServerProcessing);
                            }
                        };
                        callSnaphots(snapshots);
                    }
                    else {
                        _.delay(() => {
                            APP.trigger('savedConfiguration');
                            this._setConfigurationCookie();
                            this._setConfigurationSnapshot();
                            if (!quiet) {
                                APP.showSavedDialog(configurationFromServer);
                                waitingDialog.hide();
                            }
                            inSaveCallback(() => {
                                callback(configurationFromServer);
                            });
                        }, this.uploadSketches ? 2000 : 0);
                    }
                });
            }, noFotoSnapshots ? 0 : 500);
        }

        configuration.UserId = APP.settings.userId;

        if (method == "create") {
            configuration.id = null;
            configuration.Code = null;
        }

        if (APP.settings.userRole != 'admin') {
            configuration.Inspiration = false;
        }

        modifyBeforeWrite(configuration, method);
        APP.trigger('beforeSaveConfiguration', configuration, method);

        saveIntern()

    },

    showSavedDialog(configurationFromServer) {
        Dialogs.showOk(HBS('oc-app/configurationSafedMessage.hbs')(configurationFromServer));
    },

    /**
     * @private
     */
    _isValidationErrorAndMaybeShowError(errorMessage) {
        const validationErrors = this.getStageValidationErrors();
        if (_.isEmpty(validationErrors)) {
            return false;
        }
        if (I18N) {
            errorMessage = I18N(errorMessage);
        }
        Dialogs.showWarning(errorMessage + ' <br/> ' + validationErrors.join('<br/>'));
        return true;
    },

    /**
     * @private
     */
    _shareSocial(socialNetworkName, pleaseWaitUrl, shareUrl) {
        const width = 800;
        const height = 600;
        const win = window.open(pleaseWaitUrl, socialNetworkName, "toolbar=0, status=0, width=" + width + ", height=" + height);
        this.save({
            preferUpdate: true,
            quiet: true,
            callback: function () {
                win.location.href = shareUrl;
            }
        });
    },

    /**
     * @private
     */
    _setConfigurationCookie() {
        const configuration = this.getCurrentConfiguration();
        if (configuration?.Code) {
            this.setCookie('holodeck_configuration_code_cookie', this.getCurrentConfiguration().Code);
        }
    },

    /**
     * @private
     */
    _setConfigurationSnapshot() {
         //console.log('setConfigurationSnapshot');
        APP._configurationSnapshot = Commons.cloneDeep(this.getStageAsConfiguration());
    },

});

if (typeof AbstractApp.prototype.wkfgBackendApi === 'undefined') {
    /**
     * getter und setter für die Wkfg2BackendApi, die defaultmäßig Lazy erzeugt wird
     * @returns {*|Wkfg2BackendApi}
     */
    Object.defineProperty(AbstractApp.prototype, "wkfgBackendApi", {
        get() {
            if (this._wkfgBackendApi == null) {
                this._wkfgBackendApi = new Wkfg2BackendApi(this.settings.wkfgBackendUrl);
            }
            return this._wkfgBackendApi;
        },
        set(value) {
            this._wkfgBackendApi = value;
        }
    });
}
